import React, { useEffect, useState } from "react";
import { getDimensionsAndWeight } from "../../../common/methods";
import { AddBaggageRow } from "./AddBaggageRow";
import { useStaticImages } from "../../../hooks/useStaticImages";
import { useTranslation } from "react-i18next";
import { useStoreFly } from "../../../store";
import { 
  Button, 
  Alert, 
  CircularProgress, 
  Typography, 
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { FlightUtils } from "../../../common/FlightUtils";

// Import the bag images
import BagGreen from "../../../assets/svg2/SVG Bags from Egzonarifi.svg"; // backpack (always included)
import BagBlue from "../../../assets/svg2/SVG Bags.svg";      // blue suitcase (>=20kg)
import BagYellow from "../../../assets/svg2/SVG Bags (1).svg";  // yellow suitcase (<20kg)

export const BaggageSection = ({
  id,
  handBagDimensions = [],
  holdBagDimensions = [],
  holdBagsToPay = [],
  total,
  disableAddBaggage,
  setDisableAddBaggage,
  hideBagsToPayOption = false,
  addedBaggage = [],
  setAddedBaggage,
  currency,
  currentBnum,
  setBnum,
  flight,
  baggageInfo,
  setNoCheckedBaggage
}) => {
  const { t } = useTranslation();
  const { luggage, suitcase, disabledLuggage } = useStaticImages();
  const setAncillary = useStoreFly((state) => state.updateAncillary);
  const getAncillary = useStoreFly((state) => state.ancillary);
  const setBaggInfo = useStoreFly((state) => state.updateBaggInfo);
  const getBaggInfo = useStoreFly((state) => state.baggInfo);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [effectiveBaggageInfo, setEffectiveBaggageInfo] = useState(null);
  
  const FlightUtil = new FlightUtils();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  
  // Fix the problematic useEffect that was causing re-renders
  useEffect(() => {
    if (baggageInfo) {
      setEffectiveBaggageInfo(baggageInfo);
      setBaggInfo(baggageInfo);
    } else if (getBaggInfo && !effectiveBaggageInfo) {
      setEffectiveBaggageInfo(getBaggInfo);
    }
  }, [baggageInfo, getBaggInfo, setBaggInfo]);

  useEffect(() => {
    if (hideBagsToPayOption) {
      // remove all selected bags for this passenger
      const remaining = (getAncillary || []).filter(item => item.id !== id);
      setAncillary(remaining);
      if (setAddedBaggage) {
        setAddedBaggage([], id, 'remove-all');
      }
    }
  }, [hideBagsToPayOption]);

  // Helper function to get bag image
  // Filter baggage options to 30 kg or less
  const filterBaggageOptions = (bags = []) =>
    bags.filter((bag) => parseInt(bag.baggageWeight) <= 30);

  // Choose illustration based on weight ( <20kg = yellow, >=20kg = blue )
  const getBagImage = (weight) => {
    const weightNum = parseInt(weight);
    return weightNum < 20 ? BagYellow : BagBlue;
  };

  // Handle bag selection
  const handleBagSelection = (segmentId, bag) => {
    const pastArr = getAncillary || [];
    const isAlreadySelected = pastArr.some(
      (item) =>
        item.id === id &&
        item.segmentId === segmentId &&
        item.ancillary.ancillaryKey === bag.ancillaryKey
    );

    let updated;
      
    if (isAlreadySelected) {
      // Remove the selected bag (unselect)
      updated = pastArr.filter(
        (item) =>
          !(
            item.id === id &&
            item.segmentId === segmentId &&
            item.ancillary.ancillaryKey === bag.ancillaryKey
          )
      );
      } else {
      // Replace any existing bag for this segment with the newly selected one
      const filtered = pastArr.filter(
        (item) => !(item.id === id && item.segmentId === segmentId)
      );
      updated = [
        ...filtered,
        { id, segmentId, ancillary: bag },
      ];
    } 
      
    setAncillary(updated);

    if (hideBagsToPayOption && setNoCheckedBaggage) {
      setNoCheckedBaggage(false);
    }
  };

  // Check if bag is selected
  const isBagSelected = (segmentId, bagKey) => {
    return getAncillary?.some(item => 
      item.ancillary.ancillaryKey === bagKey &&
      item.segmentId === segmentId
    );
  };

  // Create baggage table
  const BaggageTable = ({ segment, journeyType, journeyTitle }) => (
    <Box mb={4}>
      <Typography variant="h6" fontWeight="bold" mb={2} color="#003663">
        {journeyTitle}
      </Typography>
      
      {/* Table */}
      <TableContainer component={Paper} sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
        <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%', color: '#003663' }}>{t("Baggage")}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%', color: '#003663' }}>{t("Weight")}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '35%', color: '#003663' }}>{t("Dimensions")}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%', color: '#003663' }}>{t("Price")}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '20%', color: '#003663' }}>{t("select")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Always-included personal item */}
            <TableRow key="personal-item" hover sx={{ backgroundColor: 'grey.50' }}>
              {/* Image */}
              <TableCell align="center">
                <img
                  src={BagGreen}
                  alt="personal item"
                  style={{ width: 48, height: 48, objectFit: 'contain', marginBottom: 4 }}
                />
              </TableCell>
              {/* Weight */}
              <TableCell align="center">
                <Typography variant="body2" fontWeight="bold" color="#003663">
                  {t('1x')}
                </Typography>
              </TableCell>
              {/* Dimensions / description */}
              <TableCell align="center">
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}>
                  {t('personal_item_fits_under_seat') === 'personal_item_fits_under_seat' ? t('Fits under seat') : t('personal_item_fits_under_seat')}
                </Typography>
              </TableCell>
              {/* Price */}
              <TableCell align="center">
                <Typography variant="body2" fontWeight="bold" color="#003663">
                  {t('included') === 'included' ? t('Included') : t('included')}
                </Typography>
              </TableCell>
              {/* Select column empty */}
              <TableCell align="center">✓</TableCell>
            </TableRow>

            {filterBaggageOptions(segment.baggageInfo.baggage).map((bag, index) => {
              const isSelected = isBagSelected(segment.segmentId, bag.ancillaryKey);
              
              return (
                <TableRow key={bag.ancillaryKey} hover>
                  {/* Image Column */}
                  <TableCell align="center">
                    <img
                      src={getBagImage(bag.baggageWeight)}
                      alt="baggage"
                      style={{
                        width: 48,
                        height: 48,
                        objectFit: 'contain',
                        marginBottom: 4,
                      }}
                    />
                  </TableCell>
                  
                  {/* Weight Column */}
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="bold" color="#003663">
                      {bag.baggageWeight}
                    </Typography>
                  </TableCell>
          
                  {/* Dimensions Column */}
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      wordBreak: 'break-word',
                      fontSize: '0.875rem',
                      lineHeight: 1.4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {bag.description || 
                        `Checked baggage up to ${bag.baggageWeight.toLowerCase()}`
                      }
                    </Typography>
                  </TableCell>
          
                  {/* Price Column */}
                  <TableCell align="center">
                    <Typography 
                      variant="body2" 
                      fontWeight="bold"
                      color='#003663'
                    >
                      {(Number(bag.baggagePrice + (bag.baggagePrice * 10) / 100).toFixed(2))}€
                    </Typography>
                  </TableCell>
        
                  {/* Select Column */}
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleBagSelection(segment.segmentId, bag)}
            sx={{
                        minWidth: 90,
                        borderRadius: "999px",
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        bgcolor: isSelected ? '#003663' : '#FBD215',
                        color: isSelected ? '#FBD215' : '#003663',
                        padding: "10px 20px",
                        '&:hover': {
                          bgcolor: isSelected ? '#002a4f' : '#e8c30d',
                          transform: 'scale(1.05)',
                          boxShadow: 4,
                        },
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: 2,
                      }}
                    >
                      {isSelected ? t("selected") : t("select")}
                    </Button>
                  </TableCell>
                </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // Render loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={8}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }} color="text.secondary">
          {t("loading_baggage_options")}
        </Typography>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 4 }}>
        {error}
      </Alert>
    );
  }

  // Show loading if effectiveBaggageInfo is null
  if (effectiveBaggageInfo === null) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={8}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }} color="text.secondary">
          {t("loading_baggage_options")}
        </Typography>
      </Box>
    );
  }

  // Check for specific error codes from the API
  if (effectiveBaggageInfo?.errorCode === "B070" || effectiveBaggageInfo?.errorMsg?.includes("No ancillary price")) {
    return (
      <Box sx={{ maxWidth: '4xl', mx: 'auto', px: 4, py: 8, textAlign: 'center' }}>
        <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 8 }}>
          <img src={disabledLuggage} alt="No baggage" style={{ width: 64, height: 64, margin: '0 auto 16px', opacity: 0.5 }} />
          <Typography variant="h6" color="text.primary" gutterBottom>
            {t("no_additional_baggage_available")}
          </Typography>
          <Typography color="text.secondary">
            {t("all_baggage_included")}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Check for other API errors
  if (effectiveBaggageInfo?.errorCode || effectiveBaggageInfo?.errorMsg) {
    return (
      <Box sx={{ maxWidth: '4xl', mx: 'auto', px: 4, py: 8, textAlign: 'center' }}>
        <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 8 }}>
          <img src={disabledLuggage} alt="No baggage" style={{ width: 64, height: 64, margin: '0 auto 16px', opacity: 0.5 }} />
          <Typography variant="h6" color="text.primary" gutterBottom>
            {t("baggage_temporarily_unavailable")}
          </Typography>
          <Typography color="text.secondary">
            {t("baggage_can_be_added_later")}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Check if we have baggage info (only up to 30 kg)
  const hasAnyBaggage = (effectiveBaggageInfo?.journey_0?.segmengtIds || effectiveBaggageInfo?.journey_0?.segmentIds)?.some(segment => 
    filterBaggageOptions(segment?.baggageInfo?.baggage)?.length > 0
  ) || (effectiveBaggageInfo?.journey_1?.segmengtIds || effectiveBaggageInfo?.journey_1?.segmentIds)?.some(segment => 
    filterBaggageOptions(segment?.baggageInfo?.baggage)?.length > 0
  );

  if (!hasAnyBaggage) {
    return (
      <Box sx={{ maxWidth: '4xl', mx: 'auto', px: 4, py: 8, textAlign: 'center' }}>
        <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 8 }}>
          <img src={disabledLuggage} alt="No baggage" style={{ width: 64, height: 64, margin: '0 auto 16px', opacity: 0.5 }} />
          <Typography variant="h6" color="text.primary" gutterBottom>
            {t("no_baggage_options_available")}
          </Typography>
          <Typography color="text.secondary">
            {t("all_baggage_included")}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto', px: { xs: 2, md: 4 }, py: 6 }}>
          {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom sx={{ color: '#003663' }}>
          {t("add_extra_baggage")}
        </Typography>
        {/* Promotional note */}
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontWeight: 500, fontSize: '16px' }}>
          {t("book_baggage_now_cheaper")}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Journey 0 - Outbound */}
        {(effectiveBaggageInfo?.journey_0?.segmengtIds || effectiveBaggageInfo?.journey_0?.segmentIds)?.map((segment, segIndex) => {
          const { arriveToAirportName } = FlightUtil.getAirportNameByCityCode(
            segment.departure || segment.flyFrom,
            segment.arrival || segment.flyTo,
            []
          );
          const destCity = (arriveToAirportName?.city_name || arriveToAirportName?.name || segment.arrival || segment.flyTo || '').toUpperCase();
          if (!segment?.baggageInfo?.baggage?.length) return null;
                  
                  return (
            <Box key={`journey0-${segIndex}`} sx={{ width: '100%' }}>
              {isDesktop ? (
                <BaggageTable segment={segment} journeyType="outbound" journeyTitle={`OUTBOUND TO ${destCity}`} />
              ) : (
              <>
              <Typography variant="h6" fontWeight="bold" mb={2} color="#003663" textAlign="center">
                {`OUTBOUND TO ${destCity}`}
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 2 
              }}>
                {/* Static personal item tile (mobile) */}
                <Box
                  key="personal-item"
                  sx={{
                    position: 'relative',
                    p: 3,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    bgcolor: 'primary.50',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    minHeight: 260,
                  }}
                >
                  {/* Title */}
                  <Typography variant="body1" fontWeight="bold" color="#003663" textAlign="center">
                    {t('one_personal_item', '1x personal item')}
                  </Typography>

                  {/* Subtitle */}
                  <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mb: 1 }}>
                    {t('personal_item_fits_under_seat', 'Must fit under front seat')}
                  </Typography>

                  {/* Image */}
                  <Box sx={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img
                      src={BagGreen}
                      alt="personal item"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </Box>

                  {/* Dimensions */}
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {t('personal_item_dimensions', '20 × 30 × 40 cm')}
                  </Typography>

                  {/* Included chip */}
                  <Box sx={{ mt: 'auto' }}>
                    <Typography variant="body2" fontWeight="bold" color="#003663">
                      {t('included') === 'included' ? t('Included') : t('included')}
                    </Typography>
                  </Box>
                </Box>

                {filterBaggageOptions(segment.baggageInfo.baggage).map((bag) => {
                  const isSelected = isBagSelected(segment.segmentId, bag.ancillaryKey);
                  const price = Number(bag.baggagePrice + (bag.baggagePrice * 10) / 100).toFixed(2);
                  
                  return (
                    <Box
                      key={bag.ancillaryKey}
                      onClick={() => handleBagSelection(segment.segmentId, bag)}
                      sx={{
                        position: 'relative',
                        p: 3,
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'grey.200',
                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        '&:hover': {
                          borderColor: 'primary.main',
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        }
                      }}
                    >
                      {/* Weight Badge */}
                      <Box sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: isSelected ? 'primary.main' : 'grey.100',
                        color: isSelected ? 'white' : 'text.primary',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 'full',
                        fontSize: '0.875rem',
                        fontWeight: 600
                      }}>
                        {bag.baggageWeight}
                      </Box>

                      {/* Bag Image */}
                      <Box sx={{
                        width: 64,
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <img
                          src={getBagImage(bag.baggageWeight)}
                          alt="baggage"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>

                      {/* Description */}
                      <Typography 
                        variant="body2" 
                        textAlign="center"
                        color="text.secondary"
                        sx={{ 
                          minHeight: 40,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {bag.description || `Checked baggage up to ${bag.baggageWeight}`}
                      </Typography>

                      {/* Price */}
                      <Box sx={{
                        mt: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1
                      }}>
                        <Typography 
                          variant="h6" 
                          fontWeight="bold"
                          color={isSelected ? 'primary.main' : 'text.primary'}
                        >
                          {price}€
                        </Typography>
                      </Box>

                      {/* Selected Indicator */}
                      {isSelected && (
                        <Box sx={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          bgcolor: 'primary.main',
                          color: 'white',
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.875rem',
                          fontWeight: 'bold'
                        }}>
                          ✓
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
              </>
              )}
            </Box>
                  );
                })}

        {/* Journey 1 - Return */}
        {(effectiveBaggageInfo?.journey_1?.segmengtIds || effectiveBaggageInfo?.journey_1?.segmentIds)?.map((segment, segIndex) => {
          const { arriveToAirportName } = FlightUtil.getAirportNameByCityCode(
            segment.departure || segment.flyFrom,
            segment.arrival || segment.flyTo,
            []
          );
          const destCity = (arriveToAirportName?.city_name || arriveToAirportName?.name || segment.arrival || segment.flyTo || '').toUpperCase();
          if (!segment?.baggageInfo?.baggage?.length) return null;
                  
                  return (
            <Box key={`journey1-${segIndex}`} sx={{ width: '100%' }}>
              {isDesktop ? (
                <BaggageTable segment={segment} journeyType="inbound" journeyTitle={`INBOUND TO ${destCity}`} />
              ) : (
              <>
              <Typography variant="h6" fontWeight="bold" mb={2} color="#003663" textAlign="center">
                {`INBOUND TO ${destCity}`}
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 2 
              }}>
                {/* Static personal item tile (mobile) */}
                <Box
                  key="personal-item"
                  sx={{
                    position: 'relative',
                    p: 3,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    bgcolor: 'primary.50',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    minHeight: 260,
                  }}
                >
                  {/* Title */}
                  <Typography variant="body1" fontWeight="bold" color="#003663" textAlign="center">
                    {t('one_personal_item', '1x personal item')}
                  </Typography>

                  {/* Subtitle */}
                  <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mb: 1 }}>
                    {t('personal_item_fits_under_seat', 'Must fit under front seat')}
                  </Typography>

                  {/* Image */}
                  <Box sx={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img
                      src={BagGreen}
                      alt="personal item"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </Box>

                  {/* Dimensions */}
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {t('personal_item_dimensions', '20 × 30 × 40 cm')}
                  </Typography>

                  {/* Included chip */}
                  <Box sx={{ mt: 'auto' }}>
                    <Typography variant="body2" fontWeight="bold" color="#003663">
                      {t('included') === 'included' ? t('Included') : t('included')}
                    </Typography>
                  </Box>
                </Box>

                {filterBaggageOptions(segment.baggageInfo.baggage).map((bag) => {
                  const isSelected = isBagSelected(segment.segmentId, bag.ancillaryKey);
                  const price = Number(bag.baggagePrice + (bag.baggagePrice * 10) / 100).toFixed(2);
                  
                  return (
                    <Box
                      key={bag.ancillaryKey}
                      onClick={() => handleBagSelection(segment.segmentId, bag)}
                      sx={{
                        position: 'relative',
                        p: 3,
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'grey.200',
                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        '&:hover': {
                          borderColor: 'primary.main',
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        }
                      }}
                    >
                      {/* Weight Badge */}
                      <Box sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: isSelected ? 'primary.main' : 'grey.100',
                        color: isSelected ? 'white' : 'text.primary',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 'full',
                        fontSize: '0.875rem',
                        fontWeight: 600
                      }}>
                        {bag.baggageWeight}
                      </Box>

                      {/* Bag Image */}
                      <Box sx={{
                        width: 64,
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <img
                          src={getBagImage(bag.baggageWeight)}
                          alt="baggage"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>

                      {/* Description */}
                      <Typography 
                        variant="body2" 
                        textAlign="center"
                        color="text.secondary"
                        sx={{ 
                          minHeight: 40,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {bag.description || `Checked baggage up to ${bag.baggageWeight}`}
                      </Typography>

                      {/* Price */}
                      <Box sx={{
                        mt: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1
                      }}>
                        <Typography 
                          variant="h6" 
                          fontWeight="bold"
                          color={isSelected ? 'primary.main' : 'text.primary'}
                        >
                          {price}€
                        </Typography>
                      </Box>

                      {/* Selected Indicator */}
                      {isSelected && (
                        <Box sx={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          bgcolor: 'primary.main',
                          color: 'white',
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.875rem',
                          fontWeight: 'bold'
                        }}>
                          ✓
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
              </>
              )}
            </Box>
                  );
                })}
      </Box>
    </Box>
  );
};
